import { useState } from 'react';
import {
  Plus,
  Minus,
  Loader2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/ui/user-avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { toast } from 'sonner';

interface CashMovementRecord {
  id: string;
  amount: number;
  movement_type: string;
  reason: string | null;
  created_at: string;
  user_id: string;
  user_first_name: string | null;
  user_last_name: string | null;
}

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  opening_float: 'Opening float',
  pay_in: 'Pay in',
  pay_out: 'Pay out',
  closing_float: 'Closing float',
  cash_to_bank: 'Cash to bank',
};

export default function CashManagement() {
  const { currentLocation } = useLocationStore();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const { activeSession } = useRegisterSession();
  const queryClient = useQueryClient();

  const [addCashOpen, setAddCashOpen] = useState(false);
  const [removeCashOpen, setRemoveCashOpen] = useState(false);

  const { data: movements = [], isLoading } = useQuery({
    queryKey: ['cash-movements', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          movement_type,
          reason,
          created_at,
          user_id,
          profiles:user_id (first_name, last_name)
        `)
        .eq('location_id', currentLocation.id)
        .gte('created_at', todayStart.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching cash movements:', error);
        return [];
      }

      return (data || []).map((m: any) => ({
        id: m.id,
        amount: Number(m.amount),
        movement_type: m.movement_type,
        reason: m.reason,
        created_at: m.created_at,
        user_id: m.user_id,
        user_first_name: m.profiles?.first_name || null,
        user_last_name: m.profiles?.last_name || null,
      })) as CashMovementRecord[];
    },
    enabled: !!currentLocation?.id,
    refetchInterval: 30000,
  });

  const addMovement = useMutation({
    mutationFn: async ({
      type,
      amount,
      reason,
    }: {
      type: 'pay_in' | 'pay_out';
      amount: number;
      reason: string;
    }) => {
      if (!currentLocation?.id || !user?.id || !activeSession?.id) {
        throw new Error('Missing required context');
      }

      const { error } = await supabase.from('cash_movements').insert({
        session_id: activeSession.id,
        location_id: currentLocation.id,
        user_id: user.id,
        amount,
        movement_type: type,
        reason: reason || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-movements'] });
    },
  });

  const header = (
    <div className="flex items-center justify-between gap-4">
      <h1 className="text-2xl font-display font-semibold tracking-tight">
        Cash Management
      </h1>
      <div className="flex items-center gap-2">
        <Button
          variant="destructive"
          onClick={() => setRemoveCashOpen(true)}
          disabled={!activeSession}
        >
          Remove cash
        </Button>
        <Button
          onClick={() => setAddCashOpen(true)}
          disabled={!activeSession}
        >
          Add cash
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      <p className="text-sm text-muted-foreground mb-6">
        Record your cash movements for the day.
      </p>

      {!activeSession && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 mb-6">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Open a register to add or remove cash movements.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : movements.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p>No cash movements recorded today.</p>
        </div>
      ) : (
        <div className="rounded-lg border">
          <div className="grid grid-cols-4 gap-4 border-b px-6 py-3 bg-muted/50">
            <span className="text-sm font-medium text-muted-foreground">Date</span>
            <span className="text-sm font-medium text-muted-foreground">User</span>
            <span className="text-sm font-medium text-muted-foreground">Types</span>
            <span className="text-sm font-medium text-muted-foreground text-right">Transactions ($)</span>
          </div>
          {movements.map((m) => (
            <div key={m.id} className="grid grid-cols-4 gap-4 items-center border-b last:border-b-0 px-6 py-4">
              <span className="text-sm">
                {new Date(m.created_at).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
              <div className="flex items-center gap-2">
                <UserAvatar
                  userId={m.user_id}
                  firstName={m.user_first_name}
                  lastName={m.user_last_name}
                  size="sm"
                />
                <span className="text-sm">
                  {[m.user_first_name, m.user_last_name].filter(Boolean).join(' ') || 'Unknown'}
                </span>
              </div>
              <span className="text-sm">
                {MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}
              </span>
              <span className="text-sm text-right font-medium">
                {formatCurrency(m.amount)}
              </span>
            </div>
          ))}
        </div>
      )}

      <CashMovementDialog
        open={addCashOpen}
        onOpenChange={setAddCashOpen}
        title="Add cash"
        description="Record a cash pay-in to the register."
        type="pay_in"
        onSubmit={addMovement}
      />

      <CashMovementDialog
        open={removeCashOpen}
        onOpenChange={setRemoveCashOpen}
        title="Remove cash"
        description="Record a cash pay-out from the register."
        type="pay_out"
        onSubmit={addMovement}
      />
    </DashboardLayout>
  );
}

function CashMovementDialog({
  open,
  onOpenChange,
  title,
  description,
  type,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  type: 'pay_in' | 'pay_out';
  onSubmit: { mutateAsync: (args: { type: 'pay_in' | 'pay_out'; amount: number; reason: string }) => Promise<void>; isPending: boolean };
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    try {
      await onSubmit.mutateAsync({ type, amount: parsedAmount, reason });
      toast.success(`Cash ${type === 'pay_in' ? 'added' : 'removed'} successfully`);
      setAmount('');
      setReason('');
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Failed to record cash movement', {
        description: error.message,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cash-amount">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="cash-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = val.split('.');
                  if (parts.length > 2) return;
                  if (parts[1]?.length > 2) return;
                  setAmount(val);
                }}
                className="pl-8"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cash-reason">Reason (optional)</Label>
            <Textarea
              id="cash-reason"
              placeholder="e.g. Petty cash withdrawal"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={onSubmit.isPending}
            variant={type === 'pay_out' ? 'destructive' : 'default'}
          >
            {onSubmit.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : type === 'pay_in' ? (
              <Plus className="mr-2 h-4 w-4" />
            ) : (
              <Minus className="mr-2 h-4 w-4" />
            )}
            {type === 'pay_in' ? 'Add cash' : 'Remove cash'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
